/**
 * FedEx 인보이스 추출 검증 Panel
 *
 * 데이터: fedex_service(10941) → backend proxy (/api/fedex-verify/*)
 *
 * 사용 흐름:
 *   1. 좌측에서 파일 선택 (의심 배지 = 자동 정합성 검사 실패 건)
 *   2. 중앙 원본 문서 + 우측 추출 필드 그리드
 *   3. 필드 행 클릭 → 원본 문서에서 해당 값 위치를 박스 하이라이트 (OCR 좌표 매칭)
 *   4. 액션:
 *      [정확함]     : 추출 결과 전체가 정확 (gt = extracted)
 *      [수정 저장]   : 필드 수정 후 저장 (changed_fields 자동 산출)
 *      [스킵]       : 판단 보류
 *
 * 저장: backend/fedex_verify_corrections.jsonl → /stats 에서 정확도 집계
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    ChevronLeft, ChevronRight, RefreshCw, CheckCircle, AlertTriangle,
    Edit3, SkipForward, Loader, FileText, Search, EyeOff, FileCog, X, Save,
    Table2, ListChecks, Download, FileJson, FileSpreadsheet, ZoomIn, ZoomOut, Maximize2, RotateCw, Sparkles,
} from 'lucide-react';
import RichInvoiceGrid from './RichInvoiceGrid';
import { setFedexCtx } from './fedexAssistantBus';

const API_BASE = '/api/fedex-verify';
// 검수/다운로드 대상 차수는 state(currentRound)로 관리. 기본 2차(Results_new).
const DEFAULT_ROUND = 'Results_new';
const DATASETS = [
  { id: 'IB', label: 'IB (수입)' },
  { id: 'OB', label: 'OB (수출)' },
];

// 문서타입(원본 확장자) 라벨
const DOC_TYPE = (ext) => {
  const e = (ext || '').toLowerCase();
  if (e === '.pdf') return 'PDF';
  if (e === '.tiff' || e === '.tif') return 'TIFF';
  if (e === '.xlsx' || e === '.xls') return 'XLS';
  if (e === '.jpg' || e === '.jpeg' || e === '.png') return 'IMG';
  if (e === '.docx' || e === '.doc') return 'DOC';
  return (e.replace('.', '').toUpperCase()) || '?';
};

// 품목 AUIGrid 컬럼
const COMMODITY_GRID_COLS = [
    { dataField: 'seq', headerText: 'No', width: 42, editable: false },
    { dataField: 'description', headerText: '품명', width: 220 },
    { dataField: 'tariff', headerText: 'HS코드', width: 95 },
    { dataField: 'origin', headerText: '원산지', width: 60 },
    { dataField: 'qty', headerText: '수량', width: 60, numeric: true },
    { dataField: 'unit_of_measure', headerText: '단위', width: 55 },
    { dataField: 'unit_price', headerText: '단가', width: 80, numeric: true },
    { dataField: 'value', headerText: '금액', width: 90, numeric: true },
    { dataField: 'net_weight', headerText: '순중량', width: 70, numeric: true },
    { dataField: 'PO', headerText: 'PO', width: 90 },
    { dataField: 'Part', headerText: 'Part', width: 90 },
];

// 레거시 비교에서 "노이즈"로 무시할 필드 — 레거시와 형식이 달라서 거의 항상 불일치로 잡히지만
// 실제로는 의도된 차이(주소 표기 / 무역조건 코드만 정책 / 이름 경계 규칙)라 검수 가치가 낮음.
const NOISE_FIELDS = [
    'consignee.addr', 'shipper.addr', 'importer.addr', 'buyer.addr',  // 주소 표기 차이
    'incoterm', 'tradeterm',                                           // 무역조건 (코드만 정책)
    'consignee.name', 'importer.name', 'shipper.name', 'buyer.name',   // 이름 경계(지점명/괄호)
];


// ── 텍스트 매칭 (fedex_service viewer 와 동일 로직) ──────────────

const norm = (s) => String(s).toUpperCase().replace(/[^A-Z0-9]/g, '');

// primary: 전체 값 + 줄바꿈/쉼표 분할 (값 전체가 어딘가 있으면 그것만 사용)
// fallback: 단어 쪼개기 + 숫자 변형 (primary 실패 시에만 — 동명이인 오탐 방지)
function valueSegments(value) {
    const v = String(value).trim();
    const primary = new Set();
    const fallback = new Set();
    if (norm(v).length >= 2) primary.add(v);
    v.split(/[\n,;]/).forEach(p => {
        p = p.trim();
        if (p && p !== v && norm(p).length >= 4) primary.add(p);
    });
    v.split(/\s+/).forEach(w => { if (norm(w).length >= 5) fallback.add(w); });
    const f = parseFloat(v);
    if (!isNaN(f)) {
        [String(Math.round(f)), f.toFixed(2)].forEach(s => { if (norm(s).length >= 2) fallback.add(s); });
    }
    return { primary, fallback };
}

function matchSegs(segs, ocrLines) {
    const matched = new Set();
    for (const seg of segs) {
        const segN = norm(seg);
        if (segN.length < 2) continue;
        for (const line of ocrLines) {
            const lnN = norm(line.text);
            if (lnN.length < 2) continue;
            if (segN.length < 4) {
                // 짧은 값(FCA, USD, EA 등): 라인 내 토큰 단위 완전일치 (오탐 방지)
                const tokens = String(line.text).toUpperCase().match(/[A-Z0-9]+/g) || [];
                if (lnN === segN || tokens.some(t => t === segN)) matched.add(line.idx);
            } else if (lnN.includes(segN) || (lnN.length >= 6 && segN.includes(lnN))) {
                matched.add(line.idx);
            }
        }
    }
    return matched;
}

function matchLineIdxs(value, ocrLines) {
    const sval = String(value).trim();
    // 순수 숫자: 표기(1=1.00) 무관하게 값으로 비교
    if (/^[\d,]*\.?\d+$/.test(sval)) {
        const vnum = parseFloat(sval.replace(/,/g, ''));
        if (!isNaN(vnum)) {
            const matched = new Set();
            for (const line of ocrLines) {
                const toks = String(line.text).match(/\d[\d,]*\.?\d*/g) || [];
                if (toks.some(t => Math.abs(parseFloat(t.replace(/,/g, '')) - vnum) < 0.005)) {
                    matched.add(line.idx);
                }
            }
            return matched;
        }
    }
    const { primary, fallback } = valueSegments(value);
    const m = matchSegs(primary, ocrLines);
    if (m.size) return m;
    return matchSegs(fallback, ocrLines);
}

// ── JSON flatten / path 편집 유틸 ────────────────────────────────

function flattenRows(data, prefix = '', top = '', out = []) {
    if (data === null || data === undefined) return out;
    if (Array.isArray(data)) {
        data.forEach((v, i) => flattenRows(v, `${prefix}[${i}]`, top, out));
    } else if (typeof data === 'object') {
        for (const [k, v] of Object.entries(data)) {
            flattenRows(v, prefix ? `${prefix}.${k}` : k, top || k, out);
        }
    } else {
        out.push({ path: prefix, top: top || prefix, value: data });
    }
    return out;
}

function setByPath(obj, pathStr, value) {
    const tokens = pathStr.split(/\.|\[(\d+)\]/).filter(t => t !== undefined && t !== '');
    let cur = obj;
    for (let i = 0; i < tokens.length - 1; i++) {
        cur = cur[/^\d+$/.test(tokens[i]) ? parseInt(tokens[i], 10) : tokens[i]];
        if (cur === undefined || cur === null) return;
    }
    const last = tokens[tokens.length - 1];
    const k = /^\d+$/.test(last) ? parseInt(last, 10) : last;
    const orig = cur[k];
    if (typeof orig === 'number') {
        const n = parseFloat(value);
        cur[k] = isNaN(n) ? value : n;
    } else {
        cur[k] = value;
    }
}

// ── 표 보기 (헤더 key-value + 품목 행 테이블) ────────────────────

function TableView({ extracted, onDownloadJson }) {
    if (!extracted) return <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">데이터 없음</div>;

    const flat = [];
    for (const [k, v] of Object.entries(extracted)) {
        if (k === 'commodities') continue;
        if (v && typeof v === 'object' && !Array.isArray(v)) {
            for (const [kk, vv] of Object.entries(v)) flat.push([`${k}.${kk}`, vv]);
        } else {
            flat.push([k, v]);
        }
    }
    const coms = Array.isArray(extracted.commodities)
        ? extracted.commodities.map((c, i) => ({ seq: c?.seq ?? i + 1, ...c }))
        : [];

    const gridHeight = Math.min(Math.max(coms.length * 26 + 70, 160), 420);
    return (
        <div className="flex-1 overflow-y-auto p-2">
            {/* 상단: 헤더 필드 폼 */}
            <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-600">헤더 필드</span>
                <button onClick={onDownloadJson}
                    className="flex items-center gap-1 px-2 py-0.5 text-[11px] border border-gray-300 text-gray-600 rounded hover:bg-gray-50">
                    <Download className="w-3 h-3" /> 이 건 JSON
                </button>
            </div>
            <table className="w-full text-xs border mb-3">
                <tbody>
                    {flat.map(([k, v]) => (
                        <tr key={k} className="border-b last:border-0">
                            <td className="px-2 py-1 bg-gray-50 text-gray-500 font-mono w-40 align-top">{k}</td>
                            <td className="px-2 py-1 break-all">{v === '' || v === null ? <span className="text-gray-300">∅</span> : String(v)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* 하단: 품목 AUIGrid (라인 + 가로 스크롤 + 컬럼 이동) */}
            <div className="text-xs font-semibold text-gray-600 mb-1">품목 ({coms.length})</div>
            <RichInvoiceGrid
                gridId="fedex-item-grid"
                columns={COMMODITY_GRID_COLS}
                data={coms}
                height={gridHeight}
                footerSum
                showStateColumn={false}
                autoFit={false}
            />
        </div>
    );
}

// ── Component ────────────────────────────────────────────────────

const FedexVerifyPanel = () => {
    const [files, setFiles] = useState([]);
    const [stats, setStats] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [filter, setFilter] = useState('suspect'); // all | suspect | cross | commodity | unverified | verified
    const [ignoreAddr, setIgnoreAddr] = useState(true); // 주소 필드 검증 무시
    const [search, setSearch] = useState('');
    const [rightTab, setRightTab] = useState('audit'); // audit | table
    const [rightWidth, setRightWidth] = useState(440); // 우측 패널 폭 (스플리터로 조정)
    const [zoom, setZoom] = useState(1); // 문서 확대 배율
    const [rotation, setRotation] = useState(0); // 문서 회전 (0/90/180/270)
    const dragRef = useRef(null);
    const [selectedId, setSelectedId] = useState(null);

    const [extracted, setExtracted] = useState(null);
    const [checks, setChecks] = useState(null);
    const [cross, setCross] = useState(null); // 교차검증 결과
    const [ocrLines, setOcrLines] = useState([]);
    const [page, setPage] = useState(0);
    const [pageCount, setPageCount] = useState(1);
    const [edits, setEdits] = useState({});
    const [activePath, setActivePath] = useState(null);
    // 페이지 내 단어 검색
    const [pageQuery, setPageQuery] = useState('');
    const [pageHits, setPageHits] = useState([]);   // 매칭된 ocrLine.idx 배열 (읽기순)
    const [pageHitPos, setPageHitPos] = useState(0); // 현재 포커스 매칭 위치
    const [allOcrText, setAllOcrText] = useState(''); // AI 어시스턴트용 전체 페이지 OCR 원문
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    // 프롬프트 편집 모달
    const [showPrompt, setShowPrompt] = useState(false);
    const [promptType, setPromptType] = useState('IB');
    const [promptText, setPromptText] = useState('');
    const [promptDirty, setPromptDirty] = useState(false);
    const [promptLoading, setPromptLoading] = useState(false);
    const [promptSaving, setPromptSaving] = useState(false);

    // 재추출 / 일관성(할루시네이션) 검사
    const [reextracting, setReextracting] = useState(false);
    const [consistency, setConsistency] = useState(null); // {n, total, stable, rate, unstable}
    const [opusBusy, setOpusBusy] = useState(false);
    const [opusResult, setOpusResult] = useState(null); // {opus, current, usage, saved, _show}
    const opusAbortRef = useRef(null); // 진행 중인 Opus 요청 취소용
    const [dataset, setDataset] = useState('IB');           // IB(수입) / OB(수출) 상단 탭
    const [rounds, setRounds] = useState([]);               // 차수 목록 [{src,label,round}]
    const [currentRound, setCurrentRound] = useState(DEFAULT_ROUND); // 검수 대상 차수
    const [compareBase, setCompareBase] = useState('Results'); // 비교 기준 차수 (기본: 직전 차수)
    const [invoices, setInvoices] = useState([]);           // 다중 인보이스(한 PDF에 여러 invoice no)
    const [invoiceIdx, setInvoiceIdx] = useState(0);

    const imgRef = useRef(null);
    const canvasRef = useRef(null);
    const centerRef = useRef(null);

    const showToast = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // 가운데↔우측 패널 스플리터 드래그
    const onSplitDown = useCallback((e) => {
        dragRef.current = { startX: e.clientX, startW: rightWidth };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        e.preventDefault();
    }, [rightWidth]);

    useEffect(() => {
        const onMove = (e) => {
            if (!dragRef.current) return;
            const delta = e.clientX - dragRef.current.startX;       // 오른쪽으로 끌면 +
            const next = Math.max(300, Math.min(1100, dragRef.current.startW - delta)); // 왼쪽 끌면 우측 넓어짐
            setRightWidth(next);
        };
        const onUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, []);

    // ── 목록 / 통계 ──
    const loadFiles = useCallback(async () => {
        setLoadingFiles(true);
        try {
            const [fR, cR] = await Promise.all([
                fetch(`${API_BASE}/files?src=${currentRound}`),
                fetch(`${API_BASE}/crosscheck-summary?xc=${currentRound}&base=${compareBase}`).catch(() => null),
            ]);
            const j = await fR.json();
            const crossById = new Map();
            if (cR && cR.ok) {
                try {
                    const cj = await cR.json();
                    for (const it of cj.items || []) crossById.set(it.id, it);
                } catch {}
            }
            if (j.ok) setFiles((j.items || []).map(f => ({ ...f, cross: crossById.get(f.id) || null })));
            else showToast(`목록 로드 실패: ${j.error}`, 'error');
        } catch (e) {
            showToast(`목록 로드 실패: ${e.message}`, 'error');
        } finally {
            setLoadingFiles(false);
        }
    }, [showToast, currentRound, compareBase]);

    const loadStats = useCallback(async () => {
        try {
            const r = await fetch(`${API_BASE}/stats`);
            const j = await r.json();
            if (j.ok) setStats(j);
        } catch {}
    }, []);

    useEffect(() => { loadFiles(); loadStats(); }, [loadFiles, loadStats]);

    // 차수 목록 로드 (Results* 폴더 스캔)
    useEffect(() => {
        fetch(`${API_BASE}/rounds`)
            .then(r => r.json())
            .then(j => {
                if (j.rounds?.length) {
                    setRounds(j.rounds);
                    setCurrentRound(prev =>
                        j.rounds.find(r => r.src === prev) ? prev : (j.rounds[j.rounds.length - 1]?.src || DEFAULT_ROUND)
                    );
                }
            })
            .catch(() => {});
    }, []);

    // 비교 기준(base)을 현재 차수의 "직전 차수"로 자동 설정 (사용자가 바꾸면 유지)
    useEffect(() => {
        if (!rounds.length) return;
        const curRank = (rounds.find(r => r.src === currentRound) || {}).round ?? 99;
        const prevRounds = rounds.filter(r => (r.round ?? 0) < curRank);
        const prev = prevRounds.length
            ? prevRounds.reduce((a, b) => ((a.round ?? 0) >= (b.round ?? 0) ? a : b)).src
            : 'Results';
        // compareBase가 현재 차수와 같거나 목록에 없으면 직전 차수로 재설정
        setCompareBase(cb =>
            (cb && cb !== currentRound && rounds.find(r => r.src === cb)) ? cb : prev
        );
    }, [rounds, currentRound]);

    // ── 프롬프트 편집 ──
    const loadPrompt = useCallback(async (type) => {
        setPromptLoading(true);
        try {
            const r = await fetch(`${API_BASE}/prompt?type=${type}`);
            const j = await r.json();
            setPromptText(j.text || '');
            setPromptDirty(false);
        } catch (e) {
            showToast(`프롬프트 로드 실패: ${e.message}`, 'error');
        } finally {
            setPromptLoading(false);
        }
    }, [showToast, currentRound]);

    const openPrompt = useCallback((type) => {
        setPromptType(type);
        setShowPrompt(true);
        loadPrompt(type);
    }, [loadPrompt]);

    const switchPromptType = useCallback((type) => {
        if (promptDirty && !window.confirm('저장하지 않은 변경이 있습니다. 버리고 전환할까요?')) return;
        setPromptType(type);
        loadPrompt(type);
    }, [promptDirty, loadPrompt]);

    const savePrompt = useCallback(async () => {
        setPromptSaving(true);
        try {
            const r = await fetch(`${API_BASE}/prompt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: promptType, text: promptText }),
            });
            const j = await r.json();
            if (j.saved) {
                showToast(`${promptType} 프롬프트 저장됨 (${j.bytes}B). 재추출 시 반영됩니다.`, 'success');
                setPromptDirty(false);
            } else {
                showToast(`저장 실패: ${j.error || ''}`, 'error');
            }
        } catch (e) {
            showToast(`저장 실패: ${e.message}`, 'error');
        } finally {
            setPromptSaving(false);
        }
    }, [promptType, promptText, showToast]);

    // ── 다운로드 ──
    const downloadCurrentJson = useCallback(() => {
        if (!extracted) return;
        const nm = files.find(f => f.id === selectedId)?.name || 'invoice';
        const blob = new Blob([JSON.stringify(extracted, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${nm}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
    }, [extracted, files, selectedId]);

    const downloadAll = useCallback((fmt) => {
        // 전체 최종본(Results_harness) 엑셀/JSON — backend 스트리밍
        window.location.href = `${API_BASE}/export/${fmt}?src=${currentRound}`;
    }, []);

    // ── 재추출 / 일관성 검사 ──
    const reextract = useCallback(async (n, save) => {
        if (selectedId === null) return;
        setReextracting(true);
        if (n > 1) setConsistency(null);
        try {
            const r = await fetch(`${API_BASE}/reextract/${selectedId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ n, save }),
            });
            const j = await r.json();
            if (j.ok === false) { showToast(`재추출 실패: ${j.error}`, 'error'); return; }
            if (save) {
                showToast(`재추출 완료 — 저장됨`, 'success');
                loadDetail(selectedId); // 갱신
            }
            if (n > 1 && j.consistency) {
                setConsistency({ n: j.n, ...j.consistency });
                const c = j.consistency;
                showToast(`일관성 ${c.rate}% (${c.stable}/${c.total}) — 불안정 ${c.unstable.length}필드`,
                    c.unstable.length ? 'error' : 'success');
            }
        } catch (e) {
            showToast(`재추출 실패: ${e.message}`, 'error');
        } finally {
            setReextracting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, showToast]);

    // ── Opus 교차검증 (합계 불일치 복구) ──
    const opusCrosscheck = useCallback(async (save) => {
        if (selectedId === null) return;
        const ctrl = new AbortController();
        opusAbortRef.current = ctrl;
        setOpusBusy(true);
        try {
            const r = await fetch(`${API_BASE}/opus-crosscheck/${selectedId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ save: !!save }),
                signal: ctrl.signal,
            });
            const j = await r.json();
            if (j.ok === false) { showToast(`Opus 교차 실패: ${j.error}`, 'error'); return; }
            setOpusResult({ ...j, _show: true });
            if (save && j.saved) {
                showToast(`Opus 결과 채택 — 저장됨 (품목 ${j.opus?.items}, 합계 ${j.opus?.match ? '일치' : '불일치'})`, j.opus?.match ? 'success' : 'error');
                loadDetail(selectedId);
            } else {
                showToast(`Opus 추출 완료 — 품목 ${j.opus?.items}, 합계 ${j.opus?.match ? '일치 ✓' : '불일치'}`, j.opus?.match ? 'success' : 'error');
            }
        } catch (e) {
            if (e.name === 'AbortError') { showToast('Opus 추출 중지됨 — 다시 시도할 수 있어요', 'success'); return; }
            showToast(`Opus 교차 실패: ${e.message}`, 'error');
        } finally {
            setOpusBusy(false);
            opusAbortRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, showToast]);

    // 진행 중인 Opus 요청 중지
    const cancelOpus = useCallback(() => {
        if (opusAbortRef.current) opusAbortRef.current.abort();
    }, []);

    // 이미 받은 Opus 결과를 채택(저장) — LLM 재호출 없음
    const adoptOpus = useCallback(async () => {
        if (!opusResult?.result || selectedId === null) return;
        setOpusBusy(true);
        try {
            const r = await fetch(`${API_BASE}/save-harness/${selectedId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data: opusResult.result }),
            });
            const j = await r.json();
            if (j.ok === false) { showToast(`채택 실패: ${j.error}`, 'error'); return; }
            showToast(`Opus 결과 채택 — 저장됨 (품목 ${opusResult.opus?.items})`, 'success');
            setOpusResult(o => ({ ...o, _show: false, saved: true }));
            loadDetail(selectedId);
        } catch (e) {
            showToast(`채택 실패: ${e.message}`, 'error');
        } finally {
            setOpusBusy(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opusResult, selectedId, showToast]);

    // 합계 불일치 여부 (Opus 교차 버튼 강조용)
    const sumMismatch = (checks?.rule_checks || []).some(
        c => /invoice\.value|품목\s*value|품목합/i.test(c.label || '') && c.ok === false
    );

    // ── 상세 ──
    const loadDetail = useCallback(async (id) => {
        if (id === null || id === undefined) return;
        setLoadingDetail(true);
        setExtracted(null); setChecks(null); setCross(null); setOcrLines([]);
        setEdits({}); setActivePath(null); setPage(0); setRotation(0);
        try {
            const [jsonR, pcR, ocrR] = await Promise.all([
                fetch(`${API_BASE}/json/${id}?src=${currentRound}`), // 최종본을 메인으로
                fetch(`${API_BASE}/page-count/${id}`),
                fetch(`${API_BASE}/ocr/${id}?page=0`),
            ]);
            const data = await jsonR.json();
            const arr = Array.isArray(data) ? data : [data];
            setInvoices(arr);
            setInvoiceIdx(0);
            setExtracted(arr[0] || {});
            setPageCount((await pcR.json()).count || 1);
            const ocrData = await ocrR.json();
            setOcrLines(Array.isArray(ocrData) ? ocrData : []);
            // checks(원본대조)도 최종본 기준 (OCR 폴백 있어 별도 로드)
            fetch(`${API_BASE}/checks/${id}?src=${currentRound}`)
                .then(r => r.json()).then(setChecks).catch(() => {});
            // 교차: 대상(현재 차수, xc) vs 비교 기준(compareBase) 비교
            fetch(`${API_BASE}/crosscheck/${id}?xc=${currentRound}&base=${compareBase}`)
                .then(r => r.json()).then(setCross).catch(() => {});
        } catch (e) {
            showToast(`상세 로드 실패: ${e.message}`, 'error');
        } finally {
            setLoadingDetail(false);
        }
    }, [showToast, currentRound, compareBase]);

    useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

    // 새 차수 추가 (배치 추출 연동은 Phase 3)
    const addRound = useCallback(() => {
        const maxR = rounds.length ? Math.max(...rounds.map(r => r.round || 0)) : 2;
        showToast(`${maxR + 1}차는 배치 추출로 Results_v${maxR + 1} 폴더에 생성하면 자동으로 차수 목록에 떠요`, 'success');
    }, [rounds, showToast]);

    // 다중 인보이스 전환
    const selectInvoice = useCallback((idx) => {
        setInvoiceIdx(idx);
        setExtracted(invoices[idx] || {});
        setActivePath(null);
    }, [invoices]);

    // 페이지 변경 시 OCR 재로드
    useEffect(() => {
        if (selectedId === null) return;
        fetch(`${API_BASE}/ocr/${selectedId}?page=${page}`)
            .then(r => r.json())
            .then(d => setOcrLines(Array.isArray(d) ? d : []))
            .catch(() => setOcrLines([]));
        setActivePath(null);
    }, [page, selectedId]);

    // AI 어시스턴트용: 파일의 모든 페이지 OCR 원문을 한 번 모아둠
    useEffect(() => {
        if (selectedId === null) { setAllOcrText(''); return; }
        let cancelled = false;
        const n = Math.max(1, pageCount);
        (async () => {
            try {
                const parts = await Promise.all(
                    Array.from({ length: n }, (_, p) =>
                        fetch(`${API_BASE}/ocr/${selectedId}?page=${p}`)
                            .then(r => r.json())
                            .then(d => (Array.isArray(d) ? d : []).map(l => l.text).filter(Boolean).join('\n'))
                            .catch(() => '')
                    )
                );
                if (cancelled) return;
                const text = parts
                    .map((t, p) => (n > 1 ? `--- ${p + 1}페이지 ---\n${t}` : t))
                    .filter(s => s && s.trim())
                    .join('\n\n');
                setAllOcrText(text);
            } catch { if (!cancelled) setAllOcrText(''); }
        })();
        return () => { cancelled = true; };
    }, [selectedId, pageCount]);

    // ── 캔버스 박스 하이라이트 ──
    const drawBoxes = useCallback((matchedIdx) => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img || !img.naturalWidth) return;
        const W = img.naturalWidth, H = img.naturalHeight;
        canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, W, H);
        if (!matchedIdx || !ocrLines.length) return;

        for (const line of ocrLines) {
            const isMatch = matchedIdx.has(line.idx);
            const x = line.x0 * W, y = line.y0 * H;
            const w = (line.x1 - line.x0) * W, h = (line.y1 - line.y0) * H;
            ctx.save();
            ctx.globalAlpha = isMatch ? 1.0 : 0.18;
            ctx.fillStyle = isMatch ? 'rgba(241,196,15,0.25)' : 'rgba(137,180,250,0.10)';
            ctx.fillRect(x - 1, y - 1, w + 2, h + 2);
            ctx.strokeStyle = isMatch ? '#f1c40f' : '#4a6fa5';
            ctx.lineWidth = isMatch ? 2.5 : 0.8;
            ctx.strokeRect(x - 1, y - 1, w + 2, h + 2);
            if (isMatch) {
                const label = line.text.length > 35 ? line.text.slice(0, 35) + '…' : line.text;
                ctx.font = 'bold 12px sans-serif';
                const tw = ctx.measureText(label).width;
                ctx.globalAlpha = 0.92;
                ctx.fillStyle = '#1e1e2e';
                ctx.fillRect(x - 1, y - 18, tw + 8, 16);
                ctx.globalAlpha = 1;
                ctx.fillStyle = '#f1c40f';
                ctx.fillText(label, x + 3, y - 5);
            }
            ctx.restore();
        }
    }, [ocrLines]);

    const highlightRow = useCallback((row, value) => {
        setActivePath(row.path);
        const matched = matchLineIdxs(String(value ?? row.value), ocrLines);
        drawBoxes(matched);

        if (!matched.size) {
            showToast(
                ocrLines.length
                    ? '원본에서 해당 값의 위치를 찾지 못했습니다 (OCR 미인식 또는 환각 의심)'
                    : 'OCR 라인이 없어 위치를 표시할 수 없습니다',
                'error',
            );
            return;
        }

        // 첫 매칭 박스가 보이도록 이미지 스크롤
        const first = ocrLines.find(l => matched.has(l.idx));
        const img = imgRef.current;
        const pane = centerRef.current;
        if (first && img && pane) {
            pane.scrollTo({
                top: Math.max(0, first.y0 * img.clientHeight - pane.clientHeight / 3),
                left: Math.max(0, first.x0 * img.clientWidth - pane.clientWidth / 3),
                behavior: 'smooth',
            });
        }
    }, [ocrLines, drawBoxes, showToast]);

    const handleRowClick = useCallback((row) => {
        if (activePath === row.path) {
            setActivePath(null);
            drawBoxes(null);
            return;
        }
        highlightRow(row);
    }, [activePath, drawBoxes, highlightRow]);

    // ── 현재 페이지 내 단어 검색 ──
    // 매칭 박스로 스크롤
    const scrollToLine = useCallback((idx) => {
        const line = ocrLines.find(l => l.idx === idx);
        const img = imgRef.current;
        const pane = centerRef.current;
        if (!line || !img || !pane) return;
        pane.scrollTo({
            top: Math.max(0, line.y0 * img.clientHeight - pane.clientHeight / 3),
            left: Math.max(0, line.x0 * img.clientWidth - pane.clientWidth / 3),
            behavior: 'smooth',
        });
    }, [ocrLines]);

    // 검색어로 OCR 라인 매칭 → 박스 하이라이트
    const runPageSearch = useCallback((q) => {
        const query = (q ?? '').trim().toLowerCase();
        if (!query) {
            setPageHits([]); setPageHitPos(0); drawBoxes(null); setActivePath(null);
            return;
        }
        const hits = ocrLines
            .filter(l => (l.text || '').toLowerCase().includes(query))
            .map(l => l.idx);
        setPageHits(hits);
        setPageHitPos(0);
        setActivePath(null);
        drawBoxes(new Set(hits));
        if (hits.length) scrollToLine(hits[0]);
        else showToast(`'${q.trim()}' — 이 페이지에서 찾지 못했습니다`, 'error');
    }, [ocrLines, drawBoxes, scrollToLine, showToast]);

    // 다음/이전 매칭으로 이동
    const gotoHit = useCallback((delta) => {
        if (!pageHits.length) return;
        const next = (pageHitPos + delta + pageHits.length) % pageHits.length;
        setPageHitPos(next);
        scrollToLine(pageHits[next]);
    }, [pageHits, pageHitPos, scrollToLine]);

    // 페이지/파일 전환으로 OCR 라인이 바뀌면 검색 결과 재계산
    useEffect(() => {
        if (pageQuery.trim() && ocrLines.length) runPageSearch(pageQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ocrLines]);

    // ── 파생 데이터 ──
    const currentFile = useMemo(
        () => files.find(f => f.id === selectedId) || null, [files, selectedId]);

    const rows = useMemo(() => extracted ? flattenRows(extracted) : [], [extracted]);

    // 비교 기준 차수의 표시 라벨 (예: '2차', '1차 (레거시)')
    const baseLabel = useMemo(
        () => (rounds.find(r => r.src === compareBase) || {}).label || '기준',
        [rounds, compareBase]);
    // 드롭다운 후보: 현재 차수를 제외한 모든 차수
    const baseOptions = useMemo(
        () => rounds.filter(r => r.src !== currentRound), [rounds, currentRound]);

    // AI 어시스턴트로 현재 검수 컨텍스트 발행 (파일/페이지/추출 변경 시)
    useEffect(() => {
        if (selectedId === null) { setFedexCtx(null); return; }
        // 전체 페이지 OCR이 아직 안 모였으면 현재 페이지 OCR로 폴백
        const ocrText = allOcrText || (ocrLines || []).map(l => l.text).filter(Boolean).join('\n');
        const failedRules = (currentFile?.rules_failed || []);
        const roundLabel = (rounds.find(r => r.src === currentRound) || {}).label || currentRound;
        setFedexCtx({
            id: selectedId,
            name: currentFile?.name || null,
            dataset,                 // 'IB' | 'OB'
            round: roundLabel,
            page,
            pageCount,
            extracted,               // 추출된 정형 JSON (현재 인보이스)
            ocrText,                 // 전체 페이지 OCR 원문 (폴백: 현재 페이지)
            rulesFailed: failedRules,
        });
    }, [selectedId, currentFile, dataset, currentRound, page, pageCount, extracted, ocrLines, allOcrText, rounds]);

    // 패널 떠날 때 컨텍스트 정리
    useEffect(() => () => setFedexCtx(null), []);

    // 숫자값은 표기가 달라질 수 있어 정규화 (Python str(100.0)="100.0" vs JS "100")
    const evKey = (field, value) => {
        const v = String(value).trim();
        const f = parseFloat(v);
        const canon = !isNaN(f) && String(f) !== '' && /^[\d.,-]+$/.test(v) ? String(f) : v;
        return `${field}|${canon}`;
    };

    const evidenceMap = useMemo(() => {
        const m = new Map();
        for (const e of checks?.evidence || []) m.set(evKey(e.field, e.value), e.found);
        return m;
    }, [checks]);

    // path → 교차모델 비교 결과
    const crossMap = useMemo(() => {
        const m = new Map();
        for (const f of cross?.fields || []) m.set(f.path, f);
        return m;
    }, [cross]);
    const crossAvailable = !!cross?.xc_available;

    // 무시 필드 반영한 "유효 교차 불일치" 헤더 필드 수 (commodities 제외)
    const ignoreSet = useMemo(() => new Set(ignoreAddr ? NOISE_FIELDS : []), [ignoreAddr]);
    const effMismatch = useCallback((f) => {
        if (!f.cross || !f.cross.xc_available) return null;
        return (f.cross.mismatch_paths || []).filter(
            p => !p.startsWith('commodities') && !ignoreSet.has(p)
        ).length;
    }, [ignoreSet]);
    const commodityDiff = (f) => !!(f.cross && f.cross.xc_available && !f.cross.commodity_count_match);

    // 검수 상태 분류 (unverify = 미검수로 되돌림)
    const isVerified = (f) => f.verify_status === 'correct' || f.verify_status === 'corrected';
    const isRecheck = (f) => f.verify_status === 'recheck';
    const isUnverified = (f) => !f.verify_status || f.verify_status === 'unverify';

    const matchesFilter = useCallback((f) => {
        if (filter === 'suspect') return f.rules_failed.length > 0;
        if (filter === 'cross') return (effMismatch(f) || 0) > 0;
        if (filter === 'commodity') return commodityDiff(f);
        if (filter === 'recheck') return f.verify_status === 'recheck';
        if (filter === 'unverified') return !f.verify_status || f.verify_status === 'unverify';
        if (filter === 'verified') return f.verify_status === 'correct' || f.verify_status === 'corrected';
        return true; // all
    }, [filter, effMismatch]);

    const counts = useMemo(() => {
        const c = { all: files.length, suspect: 0, cross: 0, commodity: 0, recheck: 0, unverified: 0, verified: 0 };
        for (const f of files) {
            if (f.rules_failed.length > 0) c.suspect++;
            if ((effMismatch(f) || 0) > 0) c.cross++;
            if (commodityDiff(f)) c.commodity++;
            if (f.verify_status === 'recheck') c.recheck++;
            if (!f.verify_status || f.verify_status === 'unverify') c.unverified++;
            else if (f.verify_status === 'correct' || f.verify_status === 'corrected') c.verified++;
        }
        return c;
    }, [files, effMismatch]);

    const visibleFiles = useMemo(() => {
        const q = search.trim().toLowerCase();
        return files.filter(f => {
            // IB/OB 데이터셋 탭 필터
            const isOB = (f.dataset || '').includes('OB');
            if (dataset === 'OB' && !isOB) return false;
            if (dataset === 'IB' && isOB) return false;
            if (q && !f.name.toLowerCase().includes(q)) return false;
            return matchesFilter(f);
        });
    }, [files, matchesFilter, search, dataset]);

    // ── 저장 ──

    const buildGt = useCallback(() => {
        const gt = JSON.parse(JSON.stringify(extracted));
        for (const [p, v] of Object.entries(edits)) setByPath(gt, p, v);
        return gt;
    }, [extracted, edits]);

    const save = useCallback(async (action) => {
        if (!currentFile || !extracted) return;
        setSaving(true);
        try {
            const body = {
                key: `${currentFile.dataset}/${currentFile.dir}/${currentFile.name}`,
                name: currentFile.name,
                dataset: currentFile.dataset,
                dir: currentFile.dir,
                schema_type: checks?.schema_type || null,
                action,
                extracted,
                gt: action === 'corrected' ? buildGt() : (action === 'correct' ? extracted : null),
            };
            const r = await fetch(`${API_BASE}/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const j = await r.json();
            if (j.ok) {
                const labels = { correct: '정확함', corrected: '수정 저장', skip: '스킵', recheck: '재확인 표시', unverify: '미검수로 되돌림' };
                showToast(`${labels[action] || action}${j.saved.changed_fields?.length ? ` (${j.saved.changed_fields.length}필드 수정)` : ''}`, 'success');
                setFiles(prev => prev.map(f =>
                    f.id === selectedId ? { ...f, verify_status: action } : f));
                loadStats();
                // 되돌리기(unverify)는 현재 파일 유지, 나머지는 다음 파일로
                if (action !== 'unverify') {
                    const list = visibleFiles;
                    const idx = list.findIndex(f => f.id === selectedId);
                    if (idx >= 0 && idx < list.length - 1) setSelectedId(list[idx + 1].id);
                }
            } else {
                showToast(`저장 실패: ${j.error}`, 'error');
            }
        } catch (e) {
            showToast(`저장 실패: ${e.message}`, 'error');
        } finally {
            setSaving(false);
        }
    }, [currentFile, extracted, checks, buildGt, selectedId, visibleFiles, loadStats, showToast]);

    const evidenceFor = (row) => {
        const leaf = row.path.split(/\.|\[\d+\]\.?/).filter(Boolean).pop();
        const key = evKey(leaf, row.value);
        return evidenceMap.has(key) ? evidenceMap.get(key) : null;
    };

    // ── render ──
    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
                <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <h2 className="text-lg font-semibold">FedEx 추출 검증 (TW 400건)</h2>
                    {stats && (
                        <span className="text-xs text-gray-500">
                            검수 {stats.verified}/{stats.total_files ?? '?'}건
                            {stats.file_accuracy !== null && ` · 파일 정확도 ${stats.file_accuracy}%`}
                            {stats.field_accuracy !== null && ` · 필드 정확도 ${stats.field_accuracy}%`}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => downloadAll('xlsx')}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                        title="최종본 400건 전체를 엑셀로 (헤더 반복 + 품목 행)"
                    >
                        <FileSpreadsheet className="w-3 h-3" /> 전체 엑셀
                    </button>
                    <button
                        onClick={() => downloadAll('json')}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                        title="최종본 400건 전체를 FedEx 스키마 JSON으로"
                    >
                        <FileJson className="w-3 h-3" /> 전체 JSON
                    </button>
                    <button
                        onClick={() => openPrompt('IB')}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                        title="추출 프롬프트 편집 (수정 후 재추출 시 반영)"
                    >
                        <FileCog className="w-3 h-3" /> 프롬프트 편집
                    </button>
                    <button
                        onClick={() => { loadFiles(); loadStats(); }}
                        disabled={loadingFiles}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3 h-3 ${loadingFiles ? 'animate-spin' : ''}`} /> 새로고침
                    </button>
                </div>
            </div>

            {/* IB/OB 데이터셋 탭 */}
            <div className="flex px-4 bg-white border-b">
                {DATASETS.map(ds => (
                    <button
                        key={ds.id}
                        onClick={() => { setDataset(ds.id); setSelectedId(null); }}
                        className={`px-6 py-2 text-sm font-medium border-b-2 transition ${dataset === ds.id ? 'border-orange-500 text-orange-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        {ds.label}
                    </button>
                ))}
            </div>

            {/* filters — 가로 칩 + 개수 */}
            <div className="flex gap-1.5 px-4 py-2 bg-white border-b text-xs items-center flex-wrap">
                {[
                    { id: 'all', label: '전체' },
                    { id: 'suspect', label: '규칙 의심' },
                    { id: 'cross', label: `≠${baseLabel}` },
                    { id: 'commodity', label: '품목수 불일치' },
                    { id: 'recheck', label: '재확인' },
                    { id: 'unverified', label: '미검수' },
                    { id: 'verified', label: '검수완료' },
                ].map(ft => {
                    const active = filter === ft.id;
                    return (
                        <button
                            key={ft.id}
                            onClick={() => setFilter(ft.id)}
                            className={`px-2.5 py-1 rounded-full border transition ${
                                active ? 'bg-gray-800 text-white border-gray-800 font-semibold'
                                       : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {ft.label} <span className={active ? 'font-bold' : 'text-gray-400'}>({counts[ft.id]})</span>
                        </button>
                    );
                })}

                <div className="h-4 w-px bg-gray-200 mx-1" />
                <button
                    onClick={() => setIgnoreAddr(v => !v)}
                    title="레거시와 형식만 다른 필드(주소·무역조건·이름)를 비교에서 제외 — 의도된 차이라 검수 가치 낮음"
                    className={`px-2.5 py-1 rounded-full border flex items-center gap-1 ${
                        ignoreAddr ? 'bg-gray-800 text-white border-gray-800 font-semibold' : 'bg-white text-gray-500 border-gray-300'
                    }`}
                >
                    <EyeOff className="w-3 h-3" /> 형식차이 무시 {ignoreAddr ? 'ON' : 'OFF'}
                </button>

                {/* 비교 기준 차수 선택 (현재 차수 vs 기준) */}
                {baseOptions.length > 0 && (
                    <label className="flex items-center gap-1 text-gray-500" title="현재 차수를 어느 차수와 비교할지 선택">
                        <span className="text-gray-400">비교기준</span>
                        <select
                            value={compareBase}
                            onChange={e => { setCompareBase(e.target.value); setSelectedId(null); }}
                            className="border rounded px-1.5 py-1 bg-white text-gray-700"
                        >
                            {baseOptions.map(r => (
                                <option key={r.src} value={r.src}>{r.label}</option>
                            ))}
                        </select>
                    </label>
                )}

                <div className="relative ml-auto">
                    <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="AWB 검색"
                        className="pl-6 pr-2 py-1 border rounded w-36"
                    />
                </div>
                <span className="text-gray-500">{visibleFiles.length}건</span>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* 차수 세로탭 (1차/2차/3차... + 추가) */}
                <div className="w-16 border-r bg-gray-100 flex flex-col flex-shrink-0 overflow-y-auto">
                    <div className="text-[9px] text-gray-400 text-center py-1 border-b">차수</div>
                    {rounds.map(r => (
                        <button
                            key={r.src}
                            onClick={() => { setCurrentRound(r.src); setSelectedId(null); }}
                            title={r.label}
                            className={`py-2.5 text-[11px] border-b leading-tight ${currentRound === r.src ? 'bg-white font-bold text-orange-700 border-l-2 border-orange-500' : 'text-gray-500 hover:bg-gray-200'}`}
                        >
                            {r.round}차
                        </button>
                    ))}
                    <button onClick={addRound} title="새 차수 추가" className="py-2 text-gray-400 hover:bg-gray-200 text-base">+</button>
                </div>
                {/* left: file list */}
                <div className="w-64 border-r bg-white overflow-y-auto flex-shrink-0">
                    {loadingFiles && (
                        <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                            <Loader className="w-4 h-4 animate-spin mr-2" /> 로드 중...
                        </div>
                    )}
                    {visibleFiles.map((f, i) => (
                        <button
                            key={f.id}
                            onClick={() => setSelectedId(f.id)}
                            className={`w-full text-left px-3 py-1.5 border-b hover:bg-orange-50 ${selectedId === f.id ? 'bg-orange-100' : ''}`}
                        >
                            <div className="flex items-center justify-between gap-1">
                                <span className="flex items-center gap-1.5 min-w-0">
                                    <span className="text-[10px] text-gray-400 font-mono tabular-nums flex-shrink-0 w-7 text-right" title="건별 순번">{i + 1}.</span>
                                    <span className="text-xs font-mono text-gray-700 truncate">{f.name}</span>
                                </span>
                                {isVerified(f) && <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" title="검수완료" />}
                                {isRecheck(f) && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" title="재확인 필요" />}
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 pl-[2.125rem]">
                                <span className="text-[10px] px-1 rounded bg-gray-100 text-gray-500">
                                    {f.dataset?.includes('OB') ? 'OB' : 'IB'}
                                </span>
                                <span className="text-[10px] text-gray-400 truncate">{f.dir?.split(' ')[0]}</span>
                                {f.ext && (
                                    <span className="text-[10px] px-1 rounded bg-gray-100 text-gray-500" title={`문서타입: ${f.ext}`}>
                                        {DOC_TYPE(f.ext)}
                                    </span>
                                )}
                                {f.rules_failed.length > 0 && (
                                    <span className="text-[10px] px-1 rounded bg-amber-100 text-amber-700 flex items-center gap-0.5" title="규칙검사 실패">
                                        <AlertTriangle className="w-2.5 h-2.5" />{f.rules_failed.length}
                                    </span>
                                )}
                                {(effMismatch(f) || 0) > 0 && (
                                    <span className="text-[10px] px-1 rounded bg-red-100 text-red-700" title={`${baseLabel} 대비 불일치 필드 수`}>
                                        ≠{effMismatch(f)}
                                    </span>
                                )}
                                {commodityDiff(f) && (
                                    <span className="text-[10px] px-1 rounded bg-purple-100 text-purple-700" title={`품목 수 ${f.cross.commodity_base}→${f.cross.commodity_xc}`}>
                                        품{f.cross.commodity_base}/{f.cross.commodity_xc}
                                    </span>
                                )}
                            </div>
                        </button>
                    ))}
                    {!loadingFiles && visibleFiles.length === 0 && (
                        <div className="text-center py-8 text-gray-400 text-sm">데이터 없음</div>
                    )}
                </div>

                {/* center: document image + box overlay */}
                <div className="flex-1 flex flex-col min-w-0 bg-gray-100">
                    {/* 줌 툴바 */}
                    {selectedId !== null && (
                        <div className="flex items-center gap-1 px-2 py-1 border-b bg-white flex-shrink-0">
                            <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                                className="p-1 rounded hover:bg-gray-100 text-gray-600" title="축소"><ZoomOut className="w-4 h-4" /></button>
                            <span className="text-xs text-gray-600 w-12 text-center">{Math.round(zoom * 100)}%</span>
                            <button onClick={() => setZoom(z => Math.min(4, +(z + 0.25).toFixed(2)))}
                                className="p-1 rounded hover:bg-gray-100 text-gray-600" title="확대"><ZoomIn className="w-4 h-4" /></button>
                            <button onClick={() => setZoom(1)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-600" title="원래대로(맞춤)"><Maximize2 className="w-4 h-4" /></button>
                            <div className="w-px h-4 bg-gray-200 mx-1" />
                            <button onClick={() => setRotation(r => (r + 90) % 360)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-600" title="90° 회전"><RotateCw className="w-4 h-4" /></button>
                            {rotation !== 0 && <span className="text-[11px] text-gray-500">{rotation}°</span>}
                            <span className="text-[11px] text-gray-400 ml-1 truncate max-w-[160px]">{currentFile?.name}</span>
                            {/* 페이지 내 단어 검색 */}
                            <div className="ml-auto flex items-center gap-1">
                                <div className="relative flex items-center">
                                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 pointer-events-none" />
                                    <input
                                        type="text"
                                        value={pageQuery}
                                        onChange={(e) => { setPageQuery(e.target.value); runPageSearch(e.target.value); }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); gotoHit(e.shiftKey ? -1 : 1); }
                                            else if (e.key === 'Escape') { setPageQuery(''); runPageSearch(''); }
                                        }}
                                        placeholder="이 페이지에서 검색"
                                        className="pl-7 pr-2 py-1 text-xs border rounded w-44 focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                </div>
                                {pageQuery.trim() && (
                                    <>
                                        <span className="text-[11px] text-gray-500 w-12 text-center tabular-nums">
                                            {pageHits.length ? `${pageHitPos + 1}/${pageHits.length}` : '0건'}
                                        </span>
                                        <button onClick={() => gotoHit(-1)} disabled={!pageHits.length}
                                            className="p-1 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-30" title="이전 (Shift+Enter)">
                                            <ChevronLeft className="w-4 h-4" /></button>
                                        <button onClick={() => gotoHit(1)} disabled={!pageHits.length}
                                            className="p-1 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-30" title="다음 (Enter)">
                                            <ChevronRight className="w-4 h-4" /></button>
                                        <button onClick={() => { setPageQuery(''); runPageSearch(''); }}
                                            className="p-1 rounded hover:bg-gray-100 text-gray-600" title="검색 지우기 (Esc)">
                                            <X className="w-4 h-4" /></button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <div ref={centerRef} className="flex-1 overflow-auto p-3 relative">
                    {selectedId === null ? (
                        <div className="flex items-center justify-center h-full text-gray-400">
                            좌측에서 파일을 선택하세요
                        </div>
                    ) : (
                        <>
                            <div className="relative inline-block" style={{ width: `${zoom * 100}%`, transform: rotation ? `rotate(${rotation}deg)` : undefined, transformOrigin: 'center center' }}>
                                <img
                                    ref={imgRef}
                                    src={`${API_BASE}/image/${selectedId}?page=${page}`}
                                    alt={currentFile?.name}
                                    className="block w-full shadow"
                                    style={{ maxWidth: 'none' }}
                                    onLoad={(e) => { e.target.style.display = 'block'; drawBoxes(null); }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <canvas
                                    ref={canvasRef}
                                    className="absolute top-0 left-0 w-full h-full pointer-events-none"
                                />
                            </div>
                            {pageCount > 1 && (
                                <div className="sticky bottom-0 left-0 flex items-center gap-2 mt-2 bg-white rounded px-2 py-1 shadow w-fit text-xs">
                                    <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="p-1 disabled:opacity-30">
                                        <ChevronLeft className="w-3 h-3" />
                                    </button>
                                    <span>{page + 1} / {pageCount}</span>
                                    <button onClick={() => setPage(Math.min(pageCount - 1, page + 1))} disabled={page >= pageCount - 1} className="p-1 disabled:opacity-30">
                                        <ChevronRight className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    </div>
                </div>

                {/* 스플리터: 가운데 ↔ 우측 폭 조정 */}
                <div
                    onMouseDown={onSplitDown}
                    title="드래그하여 패널 폭 조정"
                    className="w-1.5 flex-shrink-0 cursor-col-resize bg-gray-200 hover:bg-blue-400 transition-colors"
                />

                {/* right: checks + field grid */}
                <div className="border-l bg-white overflow-y-auto flex-shrink-0 flex flex-col" style={{ width: rightWidth }}>
                    {/* 우측 탭: 검수 / 표 보기 */}
                    <div className="flex border-b bg-gray-50 flex-shrink-0">
                        <button onClick={() => setRightTab('audit')}
                            className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 ${rightTab === 'audit' ? 'bg-white text-orange-700 border-b-2 border-orange-500' : 'text-gray-500 hover:bg-gray-100'}`}>
                            <ListChecks className="w-3.5 h-3.5" /> 검수
                        </button>
                        <button onClick={() => setRightTab('table')}
                            className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 ${rightTab === 'table' ? 'bg-white text-orange-700 border-b-2 border-orange-500' : 'text-gray-500 hover:bg-gray-100'}`}>
                            <Table2 className="w-3.5 h-3.5" /> 표 보기
                        </button>
                    </div>
                    {/* 다중 인보이스 네비 (한 PDF에 여러 invoice no) */}
                    {invoices.length > 1 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-purple-50 text-xs flex-shrink-0">
                            <span className="text-purple-700 font-medium">인보이스 {invoiceIdx + 1}/{invoices.length}</span>
                            <button onClick={() => selectInvoice(Math.max(0, invoiceIdx - 1))} disabled={invoiceIdx === 0}
                                className="p-0.5 rounded hover:bg-purple-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                            <button onClick={() => selectInvoice(Math.min(invoices.length - 1, invoiceIdx + 1))} disabled={invoiceIdx >= invoices.length - 1}
                                className="p-0.5 rounded hover:bg-purple-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                            <span className="font-mono text-gray-600 truncate">No. {extracted?.invoice?.num || '—'}</span>
                            <select value={invoiceIdx} onChange={e => selectInvoice(Number(e.target.value))}
                                className="ml-auto text-xs border border-purple-200 rounded px-1 py-0.5 bg-white">
                                {invoices.map((iv, i) => (
                                    <option key={i} value={i}>{i + 1}. {iv?.invoice?.num || '(no num)'} ({(iv?.commodities || []).length})</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {selectedId === null ? (
                        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                            {loadingDetail ? <Loader className="w-5 h-5 animate-spin" /> : '파일을 선택하세요'}
                        </div>
                    ) : rightTab === 'table' ? (
                        <TableView extracted={extracted} onDownloadJson={downloadCurrentJson} />
                    ) : (
                        <>
                            {/* rule checks */}
                            <div className="px-3 py-2 border-b bg-gray-50">
                                <div className="text-xs font-semibold text-gray-600 mb-1">
                                    자동 정합성 검사 {checks ? '' : '(로딩 중…)'}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {(checks?.rule_checks || []).map(c => (
                                        <span
                                            key={c.id}
                                            title={c.detail}
                                            className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                                c.ok ? 'bg-white text-gray-400 border-gray-200'
                                                     : c.level === 'warn'
                                                         ? 'bg-amber-50 text-amber-700 border-amber-300'
                                                         : 'bg-red-50 text-red-700 border-red-300'
                                            }`}
                                        >
                                            {c.ok ? '✓' : '✗'} {c.label}
                                        </span>
                                    ))}
                                </div>
                                {checks?.summary?.fields_not_found > 0 && (
                                    <div className="text-[11px] text-red-600 mt-1">
                                        ⚠ 원본에서 못 찾은 값 {checks.summary.fields_not_found}개 (환각 의심)
                                    </div>
                                )}
                                {crossAvailable ? (
                                    <div className="text-[11px] mt-1 flex items-center gap-1">
                                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                            {baseLabel} 일치 {cross.summary.agree_rate}%
                                        </span>
                                        {cross.summary.mismatched > 0 && (
                                            <span className="text-red-600">
                                                {baseLabel}와 {cross.summary.mismatched}필드 다름 — 빨간 행
                                            </span>
                                        )}
                                    </div>
                                ) : cross && (
                                    <div className="text-[10px] text-gray-400 mt-1">
                                        {baseLabel} 비교 불가
                                    </div>
                                )}
                            </div>

                            {/* field grid */}
                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-gray-100 z-10">
                                        <tr>
                                            <th className="px-2 py-1 text-left w-32">필드</th>
                                            <th className="px-2 py-1 text-left">값 (클릭=원본 위치)</th>
                                            {crossAvailable && (
                                                <th className="px-2 py-1 text-left text-gray-500" title={`비교 기준(${baseLabel}) 추출값과 비교`}>{baseLabel}</th>
                                            )}
                                            <th className="px-1 py-1 w-8" title="원본 대조">근거</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(row => {
                                            const found = evidenceFor(row);
                                            const edited = edits[row.path] !== undefined;
                                            const isActive = activePath === row.path;
                                            const xc = crossMap.get(row.path);
                                            const ignored = ignoreSet.has(row.path);
                                            const xcMismatch = crossAvailable && xc && !xc.match && !ignored;
                                            return (
                                                <tr
                                                    key={row.path}
                                                    onClick={() => handleRowClick(row)}
                                                    className={`border-b cursor-pointer ${isActive ? 'bg-yellow-50' : xcMismatch ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}
                                                >
                                                    <td className={`px-2 py-1 font-mono align-top ${ignored ? 'text-gray-300 line-through' : 'text-gray-600'}`}>
                                                        {row.path}
                                                    </td>
                                                    <td className="px-2 py-0.5 align-top">
                                                        <input
                                                            value={edits[row.path] !== undefined ? edits[row.path] : String(row.value)}
                                                            onChange={e => setEdits({ ...edits, [row.path]: e.target.value })}
                                                            onClick={e => e.stopPropagation()}
                                                            onFocus={() => {
                                                                if (activePath !== row.path) {
                                                                    highlightRow(row, edits[row.path] !== undefined ? edits[row.path] : row.value);
                                                                }
                                                            }}
                                                            className={`w-full px-1 py-0.5 border rounded font-mono text-xs ${edited ? 'border-blue-400 bg-blue-50' : xcMismatch ? 'border-red-300' : 'border-gray-200'}`}
                                                        />
                                                    </td>
                                                    {crossAvailable && (
                                                        <td className="px-2 py-0.5 align-top font-mono text-xs">
                                                            {xc ? (
                                                                xcMismatch ? (
                                                                    <button
                                                                        onClick={e => { e.stopPropagation(); setEdits({ ...edits, [row.path]: String(xc.base) }); }}
                                                                        className="text-gray-600 hover:bg-blue-50 hover:text-blue-700 rounded px-1 text-left w-full"
                                                                        title={`${baseLabel} 값으로 채택`}
                                                                    >
                                                                        {String(xc.base) || <span className="text-gray-300">∅</span>} ←
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-gray-300" title={`${baseLabel}와 일치`}>=</span>
                                                                )
                                                            ) : <span className="text-gray-200">·</span>}
                                                        </td>
                                                    )}
                                                    <td className="px-1 py-1 text-center align-top">
                                                        {found === true && <span className="text-gray-400" title="원본에서 확인됨">✓</span>}
                                                        {found === false && <span className="text-red-500" title="원본에서 못 찾음 (환각 의심)">⚠</span>}
                                                        {found === null && <span className="text-gray-300">·</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* 재추출 / 일관성(할루시네이션) 검사 */}
                            <div className="border-t p-2 flex flex-wrap gap-1 items-center bg-gray-50">
                                <span className="text-[11px] text-gray-500 mr-1">재추출:</span>
                                <button
                                    onClick={() => reextract(1, true)}
                                    disabled={reextracting}
                                    title="현재 프롬프트로 다시 추출하고 저장"
                                    className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-white disabled:opacity-50 flex items-center gap-1"
                                >
                                    <RefreshCw className={`w-3 h-3 ${reextracting ? 'animate-spin' : ''}`} /> 1회 저장
                                </button>
                                <button
                                    onClick={() => reextract(3, false)}
                                    disabled={reextracting}
                                    title="3회 추출해 매번 같은 결과인지 검사 (할루시네이션 검출, 저장 안 함)"
                                    className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-white disabled:opacity-50 flex items-center gap-1"
                                >
                                    일관성 ×3
                                </button>
                                <div className="w-px h-4 bg-gray-200 mx-1" />
                                <button
                                    onClick={() => opusCrosscheck(false)}
                                    disabled={opusBusy}
                                    title="합계 불일치 시 Opus(고해상도 비전)로 재추출 — 스캔/대량 품목 누락 복구"
                                    className={`px-2 py-1 text-xs border rounded disabled:opacity-50 flex items-center gap-1 ${sumMismatch ? 'border-amber-400 bg-amber-50 text-amber-700 font-medium' : 'border-gray-300 text-gray-600 hover:bg-white'}`}
                                >
                                    <Sparkles className={`w-3 h-3 ${opusBusy ? 'animate-spin' : ''}`} /> Opus 교차
                                </button>
                                {opusBusy && (
                                    <>
                                        <span className="text-[11px] text-amber-500">Opus 추출 중… (고해상도, 다소 느림)</span>
                                        <button
                                            onClick={cancelOpus}
                                            title="진행 중인 Opus 추출을 중지 (다시 시도 가능)"
                                            className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50 flex items-center gap-1"
                                        >
                                            <X className="w-3 h-3" /> 중지
                                        </button>
                                    </>
                                )}
                                {reextracting && <span className="text-[11px] text-gray-400">추출 중…</span>}
                                {consistency && (
                                    <button
                                        onClick={() => setConsistency(c => ({ ...c, _show: true }))}
                                        className={`px-2 py-1 text-xs rounded ml-auto ${consistency.unstable.length ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}`}
                                    >
                                        일관성 {consistency.rate}% {consistency.unstable.length ? `· 불안정 ${consistency.unstable.length}` : '· 안정'}
                                    </button>
                                )}
                            </div>

                            {/* actions */}
                            <div className="sticky bottom-0 bg-white border-t p-2 flex flex-wrap gap-1">
                                <button
                                    onClick={() => save('correct')}
                                    disabled={saving || !extracted}
                                    className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                                >
                                    <CheckCircle className="w-3 h-3" /> 정확함
                                </button>
                                <button
                                    onClick={() => save('corrected')}
                                    disabled={saving || !extracted || Object.keys(edits).length === 0}
                                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                >
                                    <Edit3 className="w-3 h-3" /> 수정 저장 {Object.keys(edits).length > 0 && `(${Object.keys(edits).length})`}
                                </button>
                                <button
                                    onClick={() => save('recheck')}
                                    disabled={saving}
                                    title="나중에 다시 확인 — '재확인' 필터로 모아서 볼 수 있음"
                                    className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                                >
                                    <AlertTriangle className="w-3 h-3" /> 재확인
                                </button>
                                <button
                                    onClick={() => save('skip')}
                                    disabled={saving}
                                    className="px-2 py-1 text-xs border border-gray-300 text-gray-500 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                                >
                                    <SkipForward className="w-3 h-3" /> 스킵
                                </button>
                                {currentFile?.verify_status && currentFile.verify_status !== 'unverify' && (
                                    <button
                                        onClick={() => save('unverify')}
                                        disabled={saving}
                                        title="검수 상태를 해제하고 미검수로 되돌림"
                                        className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                                    >
                                        <RefreshCw className="w-3 h-3" /> 미검수로
                                    </button>
                                )}
                                {Object.keys(edits).length > 0 && (
                                    <button
                                        onClick={() => setEdits({})}
                                        className="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded ml-auto"
                                    >
                                        수정 초기화
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* 일관성(할루시네이션) 상세 모달 */}
            {consistency?._show && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
                     onClick={() => setConsistency(c => ({ ...c, _show: false }))}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                            <h3 className="font-semibold text-sm">
                                일관성 검사 ({consistency.n}회 추출) — {consistency.rate}% 일치
                            </h3>
                            <button onClick={() => setConsistency(c => ({ ...c, _show: false }))} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="px-4 py-2 text-xs text-gray-500 border-b">
                            {consistency.unstable.length === 0
                                ? `${consistency.total}개 필드 모두 매번 동일 — 할루시네이션 없음 (안정적)`
                                : `${consistency.unstable.length}개 필드가 추출할 때마다 달라짐 — 할루시네이션 의심`}
                        </div>
                        <div className="flex-1 overflow-auto p-2">
                            {consistency.unstable.length === 0 ? (
                                <div className="text-center py-8 text-emerald-600 text-sm">✓ 모든 필드 안정적</div>
                            ) : (
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-100 sticky top-0">
                                        <tr>
                                            <th className="px-2 py-1 text-left w-40">필드</th>
                                            <th className="px-2 py-1 text-left">회차별 값 (불일치)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {consistency.unstable.map(u => (
                                            <tr key={u.path} className="border-b bg-red-50">
                                                <td className="px-2 py-1 font-mono align-top">{u.path}</td>
                                                <td className="px-2 py-1">
                                                    {u.values.map((v, i) => (
                                                        <div key={i} className="font-mono break-all">{i + 1}. {v || <span className="text-gray-300">∅</span>}</div>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Opus 교차검증 결과 모달 */}
            {opusResult?._show && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
                     onClick={() => setOpusResult(r => ({ ...r, _show: false }))}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-xl flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                            <h3 className="font-semibold text-sm flex items-center gap-1">
                                <Sparkles className="w-4 h-4 text-amber-500" /> Opus 교차검증 — {opusResult.name}
                            </h3>
                            <button onClick={() => setOpusResult(r => ({ ...r, _show: false }))} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="px-4 py-2 text-xs text-gray-500 border-b">
                            {opusResult.pages}페이지 · {opusResult.model} · 토큰 in {opusResult.usage?.in?.toLocaleString()} / out {opusResult.usage?.out?.toLocaleString()}
                            {opusResult.saved && <span className="ml-2 text-emerald-600 font-medium">· 채택 저장됨 ✓</span>}
                        </div>
                        <div className="p-4">
                            <table className="w-full text-xs">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-2 py-1 text-left"></th>
                                        <th className="px-2 py-1 text-right">품목</th>
                                        <th className="px-2 py-1 text-right">수량합</th>
                                        <th className="px-2 py-1 text-right">금액합</th>
                                        <th className="px-2 py-1 text-right">invoice.value</th>
                                        <th className="px-2 py-1 text-center">합계</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {opusResult.current && (
                                        <tr className="border-b text-gray-500">
                                            <td className="px-2 py-1">현재(Gemini)</td>
                                            <td className="px-2 py-1 text-right">{opusResult.current.items}</td>
                                            <td className="px-2 py-1 text-right">{opusResult.current.qty_sum}</td>
                                            <td className="px-2 py-1 text-right">{opusResult.current.val_sum?.toLocaleString()}</td>
                                            <td className="px-2 py-1 text-right">{opusResult.current.invoice_value?.toLocaleString()}</td>
                                            <td className="px-2 py-1 text-center">{opusResult.current.match ? '✓' : <span className="text-red-500">✗</span>}</td>
                                        </tr>
                                    )}
                                    <tr className={opusResult.opus?.match ? 'bg-emerald-50 font-medium' : 'bg-red-50 font-medium'}>
                                        <td className="px-2 py-1 flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-500" />Opus</td>
                                        <td className="px-2 py-1 text-right">{opusResult.opus?.items}</td>
                                        <td className="px-2 py-1 text-right">{opusResult.opus?.qty_sum}</td>
                                        <td className="px-2 py-1 text-right">{opusResult.opus?.val_sum?.toLocaleString()}</td>
                                        <td className="px-2 py-1 text-right">{opusResult.opus?.invoice_value?.toLocaleString()}</td>
                                        <td className="px-2 py-1 text-center">{opusResult.opus?.match ? <span className="text-emerald-600">일치 ✓</span> : <span className="text-red-500">불일치</span>}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="mt-2 text-[11px] text-gray-500">
                                {opusResult.opus?.match
                                    ? `Opus 금액합이 invoice.value와 일치 (운임 ${opusResult.opus?.freight?.toLocaleString()} 고려). 채택을 권장합니다.`
                                    : `Opus도 합계가 안 맞음 (차이 ${opusResult.opus?.diff?.toLocaleString()}). 스캔 품질 한계일 수 있어 사람 검수 권장.`}
                            </div>
                        </div>
                        <div className="flex gap-2 px-4 py-3 border-t bg-gray-50">
                            <button
                                onClick={adoptOpus}
                                disabled={opusBusy}
                                className={`px-3 py-1.5 text-xs rounded text-white disabled:opacity-50 flex items-center gap-1 ${opusResult.opus?.match ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                            >
                                <Sparkles className="w-3 h-3" /> 이 Opus 결과로 채택 (저장)
                            </button>
                            <button onClick={() => setOpusResult(r => ({ ...r, _show: false }))} className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded hover:bg-white">닫기</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 프롬프트 편집 모달 */}
            {showPrompt && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6"
                     onClick={() => { if (!promptDirty || window.confirm('저장하지 않은 변경이 있습니다. 닫을까요?')) setShowPrompt(false); }}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                            <div className="flex items-center gap-2">
                                <FileCog className="w-5 h-5 text-violet-600" />
                                <h3 className="font-semibold">FedEx 추출 프롬프트 편집</h3>
                                <div className="flex gap-1 ml-2">
                                    {['IB', 'OB'].map(t => (
                                        <button key={t} onClick={() => switchPromptType(t)}
                                            className={`px-2.5 py-1 text-xs rounded ${promptType === t ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                            {t === 'IB' ? 'IB (수입)' : 'OB (수출)'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <button onClick={() => { if (!promptDirty || window.confirm('저장하지 않은 변경이 있습니다. 닫을까요?')) setShowPrompt(false); }}
                                className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex-1 overflow-hidden p-3">
                            {promptLoading ? (
                                <div className="flex items-center justify-center h-full text-gray-400"><Loader className="w-6 h-6 animate-spin" /></div>
                            ) : (
                                <textarea
                                    value={promptText}
                                    onChange={e => { setPromptText(e.target.value); setPromptDirty(true); }}
                                    spellCheck={false}
                                    className="w-full h-full resize-none border rounded p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-violet-400"
                                />
                            )}
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                            <span className="text-xs text-gray-500">
                                {promptText.length}자 {promptDirty && <span className="text-amber-600">· 변경됨</span>}
                                <span className="ml-2 text-gray-400">저장 후 재추출해야 실제 추출에 반영됩니다</span>
                            </span>
                            <div className="flex gap-2">
                                <button onClick={() => loadPrompt(promptType)} disabled={promptSaving}
                                    className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200">되돌리기</button>
                                <button onClick={savePrompt} disabled={promptSaving || !promptDirty}
                                    className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1">
                                    <Save className="w-3 h-3" /> {promptSaving ? '저장 중…' : '저장'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* toast */}
            {toast && (
                <div className={`fixed bottom-4 right-4 px-3 py-2 rounded shadow text-xs z-50 ${
                    toast.type === 'error' ? 'bg-red-600 text-white' :
                    toast.type === 'success' ? 'bg-emerald-600 text-white' :
                    'bg-gray-700 text-white'
                }`}>
                    {toast.msg}
                </div>
            )}
        </div>
    );
};

export default FedexVerifyPanel;
